// Iter 31.142 (30 May 2026) — Internal-only diagnostics page.
// Iter 31.149 (30 May 2026) — Refactored to consume the shared
// <TelemetryGrid> component (the same one rendered inside the Chamber's
// Ops Board overlay). One canon cell layout, one canon action wiring,
// one source of truth for what's healthy and what's actionable. The
// page owns its chrome (title, REFRESH, footer); the grid owns the cells.
//
// Internal role only — wrapped in <Protected role="internal"> in App.js.
// Auto-refreshes every 15s so One can leave it open in a tab.
import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { RefreshCw } from "lucide-react";
import { TelemetryGrid } from "@/components/TelemetryGrid";

const REFRESH_MS = 15000;

export default function Diagnostics() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    const [lastFetch, setLastFetch] = useState(null);
    const [actionBusy, setActionBusy] = useState("");
    const [actionResult, setActionResult] = useState({});

    const load = async () => {
        setLoading(true); setErr("");
        try {
            const { data } = await api.get("/diagnostics/health");
            setData(data);
            setLastFetch(new Date());
        } catch (e) {
            setErr(`[HTTP ${e?.response?.status || "?"}] ${
                typeof e?.response?.data?.detail === "string"
                    ? e.response.data.detail
                    : (e?.response?.data?.detail?.message || e.message)
            }`);
        } finally { setLoading(false); }
    };

    useEffect(() => {
        load();
        const id = setInterval(load, REFRESH_MS);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const runAction = async (name, endpoint, formatResult) => {
        setActionBusy(name);
        try {
            const { data: out } = await api.post(endpoint);
            setActionResult((r) => ({ ...r, [name]: formatResult(out) }));
            await load();
        } catch (e) {
            const msg = typeof e?.response?.data?.detail === "string"
                ? e.response.data.detail
                : (e?.response?.data?.detail?.message || e.message);
            setActionResult((r) => ({ ...r, [name]: `Failed: ${msg}` }));
        } finally {
            setActionBusy("");
        }
    };

    return (
        <Shell>
            <div data-testid="diagnostics-page" className="space-y-5">
                <div>
                    <div className="text-[10px] tracking-[0.3em] opacity-60 mb-1">
                        // INTERNAL · SYSTEM BREATH · AUTO-REFRESH {REFRESH_MS / 1000}s
                    </div>
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <h1 className="text-3xl font-bold tracking-wide">DIAGNOSTICS.</h1>
                        <div className="flex items-center gap-3 text-[11px] opacity-70">
                            <span data-testid="diag-last-fetch">
                                {lastFetch ? `Last read · ${lastFetch.toLocaleTimeString()}` : "—"}
                            </span>
                            <button
                                data-testid="diag-refresh"
                                onClick={load}
                                disabled={loading}
                                className="inline-flex items-center gap-1 px-2 py-1 border border-ink/30 hover:border-stamp-red disabled:opacity-40 tracking-[0.15em]"
                            >
                                <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
                                REFRESH
                            </button>
                        </div>
                    </div>
                </div>

                {err && (
                    <div data-testid="diag-error" className="paper-card border border-stamp-red/40 p-3 text-sm">
                        {err}
                    </div>
                )}

                {data && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <TelemetryGrid
                            telemetry={data}
                            actionBusy={actionBusy}
                            actionResult={actionResult}
                            runAction={runAction}
                        />
                    </div>
                )}

                {data && (
                    <div className="text-[10px] tracking-[0.25em] opacity-50 pt-2">
                        Computed at {data.computed_at?.replace("T", " ").slice(0, 19)} ·
                        Primary user {data.primary_user_id?.slice(0, 8)}… ·
                        Source: <span className="font-mono">GET /api/diagnostics/health</span>
                    </div>
                )}
            </div>
        </Shell>
    );
}
